import React from "react";
import PropTypes from "prop-types";
import { css } from "@emotion/react";
import { Link } from "gatsby";
import styled from "@emotion/styled";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { theme } from "../../../utils/global-styles/theme";
import RightGoToArrow from "../../CourseCard/Icons/RightGoToArrow";
import {
  determineProductPath,
  determineProductType,
} from "../../../utils/services/methods";

const MobileCard = ({
  languageCode = "en-US",
  slug,
  title,
  courseCost,
  courseImage,
  courseDuration,
  courseType,
  courseAvailability,
  backgroundColorClass,
  courseIcon,
  courseIndex,
  darkImageOverlayText,
  cta,
}) => {
  const courseIconReduction = 30 / 35;
  courseIcon = React.cloneElement(courseIcon, {
    width: parseFloat(courseIcon.props.width) * courseIconReduction + "px",
    height: parseFloat(courseIcon.props.height) * courseIconReduction + "px",
  });
  const MobileImageBackgroundWrapper = styled("div")`
      display: grid;
      h2,
      p {
        color: ${theme.colors.main};
      }
    `,
    MobileImageBackground = styled(GatsbyImage)`
      grid-area: 1 / 1 / span 1 / span 3;
      height: 234px;
      display: grid;
    `,
    MobileCardWrapper = styled(Link)`
      grid-area: 1 / 1 / span 1 / span 3;
      height: 234px;
      display: grid;
      grid-template: 60px 3fr 1fr 1fr / 58px auto 10%;
      z-index: 1;
    `,
    MobileCardCourseTypeText = styled("p")`
      font-size: 14px;
      font-weight: normal;
      font-family: ${theme.fontFaces.headers};
      grid-area: 1 / 2 / span 1;
      display: flex;
      align-items: center;
    `,
    MobileCardDetailsWrapper = styled("div")`
      padding-bottom: 20px;
      grid-area: 4 / 2 / span 1;
      display: flex;
      flex-direction: row;
      p {
        border-top-width: 4px;
        border-top-style: solid;
        padding-top: 11px;
        margin-right: 20px;
        font-size: 12px;
        line-height: 14px;
        font-family: ${theme.fontFaces.headers};
        font-weight: bold;
      }
    `,
    EnrollNowContainer = styled.div`
      grid-area: 3 / 2 / span 1 / span 1;
      align-self: end;
      padding: 10px 0;

      @media only screen and (max-width: ${theme.breakpoints.iPadPro}) {
        display: flex;
        align-items: center;
      }
    `,
    EnrollNowText = styled.p`
      padding-right: 1em;
      display: inline;
      font-size: 12px;
      line-height: 14px;
      font-family: ${theme.fontFaces.headers};
      font-weight: bold;
    `,
    type = determineProductType(courseType),
    to = cta?.url || determineProductPath(type, languageCode, slug);

  return courseIndex % 3 === 0 ? (
    // If this card's index in the list is a multiple of 3, we give it an image background
    <MobileImageBackgroundWrapper>
      <MobileImageBackground image={getImage(courseImage.gatsbyImageData)} />
      <MobileCardWrapper className="mrkt-course-card" to={`/course/${slug}/`}>
        <MobileCardCourseTypeText>{courseType}</MobileCardCourseTypeText>
        <h2
          css={css`
            grid-area: 2 / 2 / span 1 / span 1;
            font-size: 20px;
            line-height: 24px;
          `}
        >
          {title}
        </h2>
        <EnrollNowContainer>
          <EnrollNowText>{cta?.linkText || "Enroll Now"}</EnrollNowText>
          <RightGoToArrow fillColor={"primary-fill"} />
        </EnrollNowContainer>
        <MobileCardDetailsWrapper>
          <p className={` ${"main-border"} primary`}>{courseCost}</p>
          <p className={` ${"main-border"} primary`}>{courseAvailability}</p>
          <p className={` ${"main-border"} primary`}>{courseDuration}</p>
        </MobileCardDetailsWrapper>
      </MobileCardWrapper>
    </MobileImageBackgroundWrapper>
  ) : (
    //If this card's index is not a multiple of 3, we just give it a background color, no image background
    <MobileCardWrapper
      className={`mrkt-course-card ${backgroundColorClass}`}
      to={`${to}`}
    >
      <MobileCardCourseTypeText
        className={` ${
          backgroundColorClass === "course-catalog-primary-Mobile-card"
            ? "white"
            : "main"
        }`}
      >
        {courseType}
      </MobileCardCourseTypeText>
      <h2
        css={css`
          grid-area: 2 / 2 / span 1 / span 1;
          font-size: 20px;
          line-height: 24px;
        `}
      >
        {title}
      </h2>
      <EnrollNowContainer>
        <EnrollNowText className="main">
          {cta?.linkText || "Enroll Now"}
        </EnrollNowText>
        <RightGoToArrow fillColor={"primary-fill"} />
      </EnrollNowContainer>
      <MobileCardDetailsWrapper>
        <p className={`main main-border`}>{courseCost}</p>
        <p className={`main main-border`}>{courseAvailability}</p>
        <p className={`main main-border`}>{courseDuration}</p>
      </MobileCardDetailsWrapper>
    </MobileCardWrapper>
  );
};

MobileCard.propTypes = {
  slug: PropTypes.string,
  title: PropTypes.string,
  courseCost: PropTypes.string,
  courseDuration: PropTypes.string,
  courseType: PropTypes.string,
  courseAvailability: PropTypes.string,
  backgroundColorClass: PropTypes.string,
  courseIcon: PropTypes.element,
  courseIndex: PropTypes.number,
  courseImage: PropTypes.shape({
    fluid: PropTypes.shape({ srcSet: PropTypes.string }),
    description: PropTypes.string,
  }),
};

export default MobileCard;
